import { useId, useState } from 'react'

import { cn } from '@/shared/lib/cn'
import {
  LIENZO,
  altoUtil,
  anchoUtil,
  indiceMasCercano,
  pathDeArea,
  pathDeLinea,
  posX,
  posY,
  ticksY,
  topeY,
} from './scales'

export interface SerieTendencia {
  /** Nombre visible. Va en la leyenda y en el tooltip. */
  label: string
  /** `null` = no hay dato esa semana. No se interpola: corta la línea. */
  valores: readonly (number | null)[]
  /**
   * `serie` es la que cuenta la historia; `contexto` es la referencia contra la
   * que se lee. Nunca dos `serie` en el mismo gráfico: dos hues compitiendo es
   * justamente lo que esta app no hace (ver la nota de `tokens.css`).
   */
  rol: 'serie' | 'contexto' | 'alerta'
  /** El área bajo la línea, a 10% de opacidad. Sólo para una serie por gráfico. */
  conArea?: boolean
  /** Sufijo del valor en el tooltip ("%", "d"). */
  sufijo?: string
}

export interface TrendChartProps {
  series: readonly SerieTendencia[]
  /** Una etiqueta por punto del eje X. */
  etiquetas: readonly string[]
  /** Descripción para lectores de pantalla: lo que el gráfico dice. */
  resumenAccesible: string
  /** Fuerza el tope del eje (para porcentajes, 100). */
  topeFijo?: number
  className?: string
}

const COLOR: Record<SerieTendencia['rol'], string> = {
  serie: 'hsl(var(--chart-serie))',
  contexto: 'hsl(var(--chart-contexto))',
  alerta: 'hsl(var(--overdue))',
}

/**
 * Línea (o área) sobre un eje de tiempo, con lectura al pasar el mouse.
 *
 * **Por qué no hay una librería de gráficos acá.** La app dibuja con los tokens
 * de "Consola" —hairlines, mono para los números, dos temas definidos a mano— y
 * cualquier librería trae su propio sistema visual que después hay que apagar
 * pieza por pieza. Lo que se necesitaba era un eje, una línea, un área y un
 * tooltip: eso es menos código que la configuración para desactivar lo demás, y
 * no suma 100kB al bundle de una herramienta interna.
 *
 * El hover no es un adorno: un gráfico de doce semanas sin lectura puntual
 * obliga a estimar contra el eje, y entonces el dato exacto —que existe— queda
 * del otro lado de una conjetura.
 */
export function TrendChart({
  series,
  etiquetas,
  resumenAccesible,
  topeFijo,
  className,
}: TrendChartProps) {
  const id = useId()
  const [activo, setActivo] = useState<number | null>(null)

  const n = etiquetas.length
  const planos = series.flatMap((s) => s.valores.filter((v): v is number => v !== null))
  const tope = topeFijo ?? topeY(planos)
  const marcas = ticksY(tope)

  if (n === 0) return null

  return (
    <figure className={cn('flex flex-col gap-2', className)}>
      {/* La leyenda existe siempre que haya dos series: la identidad no puede
          depender sólo del color. Con una sola, el título ya la nombra. */}
      {series.length > 1 ? (
        <figcaption className="flex flex-wrap items-center gap-3 text-2xs text-text-2">
          {series.map((serie) => (
            <span key={serie.label} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-0.5 w-3 rounded-full"
                style={{ backgroundColor: COLOR[serie.rol] }}
              />
              {serie.label}
            </span>
          ))}
        </figcaption>
      ) : null}

      <svg
        viewBox={`0 0 ${LIENZO.ancho} ${LIENZO.alto}`}
        className="w-full"
        style={{ height: 'auto' }}
        role="img"
        aria-label={resumenAccesible}
        onMouseLeave={() => setActivo(null)}
        onMouseMove={(evento) => {
          const caja = evento.currentTarget.getBoundingClientRect()
          // De píxeles de pantalla a unidades del viewBox.
          const x = ((evento.clientX - caja.left) / caja.width) * LIENZO.ancho
          setActivo(indiceMasCercano(x, n))
        }}
      >
        {/* Grilla: hairline, sólida y recesiva. Nunca punteada. */}
        {marcas.map((marca) => {
          const y = posY(marca, tope)
          return (
            <g key={marca}>
              <line
                x1={LIENZO.margen.izquierda}
                x2={LIENZO.margen.izquierda + anchoUtil()}
                y1={y}
                y2={y}
                stroke="hsl(var(--chart-grid))"
                strokeWidth={1}
              />
              <text
                x={LIENZO.margen.izquierda - 6}
                y={y + 3}
                textAnchor="end"
                className="num"
                fontSize={9}
                fill="hsl(var(--text-3))"
              >
                {marca}
              </text>
            </g>
          )
        })}

        {/* La franja de la semana apuntada, debajo de las marcas. */}
        {activo !== null ? (
          <line
            x1={posX(activo, n)}
            x2={posX(activo, n)}
            y1={LIENZO.margen.arriba}
            y2={LIENZO.margen.arriba + altoUtil()}
            stroke="hsl(var(--text-3))"
            strokeWidth={1}
            strokeOpacity={0.5}
          />
        ) : null}

        {series.map((serie) => {
          const color = COLOR[serie.rol]
          const solidos = serie.valores.map((v) => (v === null ? 0 : v))
          return (
            <g key={serie.label}>
              {serie.conArea ? (
                <path d={pathDeArea(solidos, tope)} fill={color} fillOpacity={0.1} />
              ) : null}
              <path
                d={pathDeLinea(serie.valores, tope)}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          )
        })}

        {/* El punto apuntado, con anillo del color de la superficie para que se
            lea aunque caiga encima de otra línea. */}
        {activo !== null
          ? series.map((serie) => {
              const valor = serie.valores[activo]
              if (valor === null || valor === undefined) return null
              return (
                <circle
                  key={`${id}-${serie.label}`}
                  cx={posX(activo, n)}
                  cy={posY(valor, tope)}
                  r={4}
                  fill={COLOR[serie.rol]}
                  stroke="hsl(var(--surface-1))"
                  strokeWidth={2}
                />
              )
            })
          : null}

        {/* Eje X: sólo el primero, el último y el apuntado. Doce etiquetas de
            fecha una al lado de la otra no se leen, se pisan. */}
        {etiquetas.map((etiqueta, i) => {
          const visible = i === 0 || i === n - 1 || i === activo
          if (!visible) return null
          return (
            <text
              key={etiqueta}
              x={posX(i, n)}
              y={LIENZO.alto - 6}
              textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
              className="num"
              fontSize={9}
              fill="hsl(var(--text-3))"
            >
              {etiqueta}
            </text>
          )
        })}
      </svg>

      {/*
        La lectura va en HTML y no dentro del SVG: así hereda la tipografía y
        los tokens del resto de la app, y no hay que reimplementar el ajuste de
        texto a mano. Ocupa su lugar SIEMPRE —aunque esté vacía— para que el
        gráfico no salte hacia arriba cuando el mouse entra.
      */}
      <p className="min-h-4 text-2xs text-text-2" aria-live="off">
        {activo !== null ? (
          <>
            <span className="num text-text-3">{etiquetas[activo]}</span>
            {series.map((serie) => {
              const valor = serie.valores[activo]
              return (
                <span key={serie.label} className="ml-3">
                  {serie.label}{' '}
                  <span className="num text-text-1">
                    {valor === null || valor === undefined ? '—' : `${valor}${serie.sufijo ?? ''}`}
                  </span>
                </span>
              )
            })}
          </>
        ) : null}
      </p>
    </figure>
  )
}
